"use client";

import Link from "next/link";
import Image from "next/image";
import { useState } from "react";
import { useLanguage } from "@/app/context/LanguageContext";

import { Phone, Mail, MapPin, Send } from "lucide-react";

const Instagram = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
  </svg>
);

const Facebook = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
  </svg>
);

const Youtube = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-1.96C18.88 4 12 4 12 4s-6.88 0-8.6.46A2.78 2.78 0 0 0 1.46 6.42 29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58 2.78 2.78 0 0 0 1.94 1.96C5.12 20 12 20 12 20s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-1.96A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z" />
    <polygon points="10 15 15 12 10 9 10 15" />
  </svg>
);

const socialLinks = [
  { icon: Instagram, href: "https://www.instagram.com/sri_18_padikaruppanasamy/", name: "Instagram" },
  { icon: Facebook, href: "https://www.facebook.com/profile.php?id=61590469809766", name: "Facebook" },
  { icon: Youtube, href: "https://www.youtube.com/@Karuppanasami", name: "YouTube" },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const { t } = useLanguage();

  const quickLinks = [
    { name: t.nav.home, href: "/" },
    { name: t.nav.about, href: "/about" },
    { name: t.nav.poojaSchedule, href: "/pooja-schedule" },
    { name: t.nav.events, href: "/events" },
    { name: t.nav.imageGallery, href: "/gallery/image-gallery" },
    { name: t.nav.videoGallery, href: "/gallery/video-gallery" },
    { name: t.nav.contact, href: "/contact" },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError("");
    setIsSubmitting(true);

    try {
      const response = await fetch("/api/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = (await response.json()) as { error?: string };

      if (!response.ok) {
        throw new Error(data.error ?? "Failed to subscribe");
      }

      setSubscribed(true);
      setEmail("");
      setTimeout(() => setSubscribed(false), 5000);
    } catch {
      setSubmitError(t.footer.subscribeError);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="relative overflow-hidden bg-[#140d07] text-white">
      <div className="absolute top-0 left-1/2 h-px w-full max-w-7xl -translate-x-1/2 bg-gradient-to-r from-transparent via-amber-400/60 to-transparent" />
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 left-0 h-72 w-72 rounded-full bg-orange-500 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-72 w-72 rounded-full bg-red-500 blur-3xl" />
      </div>

      <div className="relative max-w-[1400px] mx-auto px-4 lg:px-8 py-16">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-3">
          <div className="space-y-8">
            <div className="space-y-5">
              <Link href="/" className="inline-flex items-center gap-4">
                <Image
                  src="/logo.png"
                  alt="Sri 18m Padi Karuppanasamy Temple"
                  width={70}
                  height={70}
                  className="h-16 w-auto object-contain"
                />

              </Link>
              <p className="max-w-md text-sm leading-7 text-white/70">
                {t.footer.desc}
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-3 text-sm text-white/70">
                <Phone className="h-4 w-4 text-amber-400" />
                <span>+91 95972 55401</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-white/70">
                <Mail className="h-4 w-4 text-amber-400" />
                <span>karuppanasamytemple@gmail.com</span>
              </div>
              <div className="flex items-start gap-3 text-sm text-white/70">
                <MapPin className="h-4 w-4 text-amber-400 mt-1" />
                <span>{t.footer.address}</span>
              </div>
            </div>

            <div className="flex items-center gap-4">
              {socialLinks.map((social) => (
                <Link
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  className="h-11 w-11 rounded-full border border-white/10 bg-white/5 flex items-center justify-center hover:bg-amber-500 hover:text-black transition-all duration-300"
                >
                  <social.icon className="h-5 w-5" />
                </Link>
              ))}
            </div>
          </div>

          <div className="space-y-8">
            <h3 className="text-lg font-bold text-amber-400">
              {t.footer.quickLinks}
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {quickLinks.map((link) => (
                <Link
                  key={link.name}
                  href={link.href}
                  className="text-sm text-white/70 hover:text-white hover:translate-x-1 transition-all duration-300"
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>

          <div className="space-y-8">
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-amber-400">
                {t.footer.updates}
              </h3>
              <p className="text-sm leading-7 text-white/70">
                {t.footer.updatesDesc}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {subscribed ? (
                <p className="rounded-full border border-green-800/40 bg-green-950/20 px-6 py-4 text-sm text-green-400">
                  {t.footer.subscribeSuccess}
                </p>
              ) : (
                <>
                  <div className="relative">
                    <input
                      type="email"
                      required
                      placeholder={t.footer.emailPlaceholder}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={isSubmitting}
                      className="w-full h-14 rounded-full border border-white/10 bg-white/5 px-6 pr-14 text-white placeholder:text-white/40 outline-none focus:border-amber-400 transition disabled:opacity-70"
                    />
                    <Mail className="absolute right-5 top-1/2 -translate-y-1/2 h-5 w-5 text-white/40" />
                  </div>
                  {submitError ? (
                    <p className="text-red-400 text-sm">{submitError}</p>
                  ) : null}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full h-14 rounded-full bg-gradient-to-r from-amber-600 via-orange-500 to-red-500 font-semibold flex items-center justify-center gap-2 hover:scale-[1.02] transition-all duration-300 disabled:opacity-70 disabled:hover:scale-100"
                  >
                    {isSubmitting ? t.footer.subscribing : t.footer.subscribe}
                    <Send className="h-4 w-4" />
                  </button>
                </>
              )}
            </form>

            <div className="flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-5 py-4">
              <div className="h-3 w-3 rounded-full bg-green-500 animate-pulse" />
              <span className="text-sm font-medium text-white/80">
                {t.footer.templeStatus}
              </span>
            </div>
          </div>
        </div>

        <div className="mt-14 border-t border-white/10 pt-8 flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
          <p className="text-sm text-white/50 text-center md:text-left">
            {t.footer.copyright.replace(
              "2026",
              new Date().getFullYear().toString(),
            )}
          </p>
          <div className="flex items-center justify-center gap-6 text-sm text-white/50">
            <Link
              href="/privacy-policy"
              className="hover:text-white transition"
            >
              {t.footer.privacy}
            </Link>
            <Link
              href="/terms-and-conditions"
              className="hover:text-white transition"
            >
              {t.footer.terms}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
